import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import Seo from "@/components/Seo";
import { FadeUp } from "@/components/Reveal";
import { Eyebrow, LuxeButton } from "@/components/shared";
import { ArrowLeft, ArrowRight, Facebook, Linkedin, Twitter } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (d) =>
  new Date(d + "T12:00:00").toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

const ArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setArticle(null);
    setNotFound(false);
    axios
      .get(`${API}/articles/${slug}`)
      .then((r) => {
        setArticle(r.data);
        return axios.get(`${API}/articles`);
      })
      .then((r) => {
        setArticle((current) => {
          if (current) setRelated(r.data.filter((a) => current.related.includes(a.slug)));
          return current;
        });
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return (
      <div className="container-luxe flex min-h-[70vh] flex-col items-start justify-center pt-32" data-testid="article-not-found">
        <h1 className="font-serif text-5xl font-light text-charcoal">Story Not Found</h1>
        <div className="mt-8">
          <LuxeButton to="/news" variant="dark" testId="article-back-btn">Back to News</LuxeButton>
        </div>
      </div>
    );

  if (!article)
    return (
      <div className="container-luxe flex min-h-[70vh] items-center pt-32" data-testid="article-loading">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-slate-muted">Loading…</p>
      </div>
    );

  const shareUrl = typeof window !== "undefined" ? encodeURIComponent(window.location.href) : "";

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: article.title,
        description: article.meta_description,
        image: article.image,
        datePublished: article.date,
        author: { "@type": "Person", name: article.author },
        publisher: { "@type": "Organization", name: "Scherif Real Estate" },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "/" },
          { "@type": "ListItem", position: 2, name: "News", item: "/news" },
          { "@type": "ListItem", position: 3, name: article.title },
        ],
      },
    ],
  };

  return (
    <>
      <Seo title={article.seo_title} siteName="Scherif Real Estate" description={article.meta_description} image={article.image} type="article" jsonLd={jsonLd} />

      <article className="container-luxe max-w-4xl pb-24 pt-36 sm:pt-44" data-testid="article-page">
        <FadeUp>
          <nav aria-label="Breadcrumb" className="mb-10">
            <Link to="/news" data-testid="article-breadcrumb-news" className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-muted transition-colors hover:text-charcoal">
              <ArrowLeft size={12} strokeWidth={1.5} aria-hidden="true" /> News — {article.category}
            </Link>
          </nav>
          <Eyebrow className="mb-5">{article.category}</Eyebrow>
          <h1 className="font-serif text-4xl font-light leading-[1.12] tracking-tight text-charcoal sm:text-5xl" data-testid="article-title">
            {article.title}
          </h1>
          <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-muted">
            By {article.author} — {formatDate(article.date)}
          </p>
        </FadeUp>

        <FadeUp delay={0.15} className="mt-12">
          <img src={article.image} alt={article.title} className="aspect-[16/9] w-full object-cover" />
        </FadeUp>

        <div className="mt-14 space-y-7" data-testid="article-body">
          {article.body.map((p, i) => (
            <p key={i} className={`text-base leading-[1.9] text-charcoal/85 ${i === 0 ? "first-letter:float-left first-letter:mr-3 first-letter:font-serif first-letter:text-6xl first-letter:font-light first-letter:leading-[0.85] first-letter:text-champagne" : ""}`}>
              {p}
            </p>
          ))}
        </div>

        <div className="mt-14 flex flex-wrap items-center gap-6 border-y border-warm-border py-8">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-muted">Share</span>
          <a href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${encodeURIComponent(article.title)}`} target="_blank" rel="noopener noreferrer" aria-label="Share on X" data-testid="share-x" className="text-charcoal/60 transition-colors hover:text-champagne">
            <Twitter size={18} strokeWidth={1.25} />
          </a>
          <a href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook" data-testid="share-facebook" className="text-charcoal/60 transition-colors hover:text-champagne">
            <Facebook size={18} strokeWidth={1.25} />
          </a>
          <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`} target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn" data-testid="share-linkedin" className="text-charcoal/60 transition-colors hover:text-champagne">
            <Linkedin size={18} strokeWidth={1.25} />
          </a>
        </div>

        <FadeUp className="mt-14 border border-warm-border bg-stone-beige p-8 sm:p-10">
          <p className="font-serif text-2xl font-light text-charcoal">Thinking about your next move?</p>
          <p className="mt-3 max-w-lg text-sm leading-relaxed text-slate-muted">
            Whether buying, selling, or exploring, begin with a conversation.
          </p>
          <div className="mt-6 flex flex-col gap-4 sm:flex-row">
            <LuxeButton to="/listings" variant="dark" testId="article-listings-cta">Browse Listings</LuxeButton>
            <LuxeButton to="/contact" variant="outline-dark" testId="article-contact-cta">Contact Soby</LuxeButton>
          </div>
        </FadeUp>

        {related.length > 0 && (
          <div className="mt-20" data-testid="related-articles">
            <h2 className="mb-10 font-serif text-3xl font-light text-charcoal">Continue Reading</h2>
            <div className="grid gap-10 sm:grid-cols-2">
              {related.map((r) => (
                <Link key={r.slug} to={`/news/${r.slug}`} className="group block" data-testid={`related-${r.slug}`}>
                  <div className="img-zoom">
                    <img src={r.image} alt={r.title} className="aspect-[16/10] w-full object-cover" loading="lazy" />
                  </div>
                  <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.3em] text-champagne">{r.category}</p>
                  <h3 className="mt-2 font-serif text-2xl font-light leading-snug text-charcoal transition-colors group-hover:text-champagne">{r.title}</h3>
                  <p className="mt-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-charcoal">
                    Read More <ArrowRight size={12} strokeWidth={1.5} className="transition-transform duration-500 group-hover:translate-x-2" aria-hidden="true" />
                  </p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </>
  );
};

export default ArticlePage;
